import React from 'react';
import { Typography, TypographyProps, Tooltip, useTheme, SxProps } from '@mui/material';

export type TruncatedCellContentProps = {
    content: React.ReactNode;
    textCellVariant?: TypographyProps['variant'];
    columnRowReverse?: boolean;
    isCellHovered?: boolean;
    hasOnClickCell?: boolean;
    rowHover?: boolean;
    backgroundColor?: string;
    maxWidth?: string;
    sx?: SxProps;
};

export const TruncatedCellContent: React.FunctionComponent<TruncatedCellContentProps> = function ({
    content,
    textCellVariant,
    columnRowReverse,
    isCellHovered = false,
    hasOnClickCell = false,
    rowHover = false,
    backgroundColor,
    maxWidth = '200px',
    sx,
}) {
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const contentRef = React.useRef<HTMLSpanElement>(null);
    const { palette } = useTheme();

    React.useLayoutEffect(() => {
        if (contentRef.current) {
            const element = contentRef.current;
            const overflowing = element.scrollWidth > element.clientWidth;
            setIsOverflowing(overflowing);
        }
    }, [content]);

    const gradientColor =
        backgroundColor ||
        `var(--row-fade-color, ${
            rowHover ? palette.background.default : palette.background.paper
        })`;

    const cellContent = (
        <Typography
            variant={textCellVariant || 'l2dcaption'}
            sx={{
                display: 'flex',
                flexDirection: columnRowReverse ? 'row-reverse' : 'row',
                maxWidth,
                overflow: 'hidden',
                textOverflow: isOverflowing ? 'ellipsis' : 'clip',
                whiteSpace: 'nowrap',
                position: 'relative',
                transition: 'color 0.3s ease, text-shadow 0.3s ease',
                ...(isCellHovered &&
                    hasOnClickCell && {
                        color: palette.primary.main,
                    }),
                ...(isOverflowing && {
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '20px',
                        background: `linear-gradient(to right, transparent, ${gradientColor})`,
                        pointerEvents: 'none',
                        transition: 'background 0.3s ease',
                    },
                }),
                ...sx,
            }}
        >
            <span
                ref={contentRef}
                style={{
                    display: 'inline-block',
                    maxWidth,
                    overflow: 'visible',
                    whiteSpace: 'nowrap',
                }}
            >
                {content}
            </span>
        </Typography>
    );

    if (isOverflowing) {
        return (
            <Tooltip
                title={content}
                enterDelay={500}
                leaveDelay={200}
                componentsProps={{
                    tooltip: {
                        sx: {
                            color: palette.text.primary,
                            backgroundColor: palette.background.paper,
                            border: '1px solid',
                            borderColor: 'divider',
                        },
                    },
                }}
            >
                {cellContent}
            </Tooltip>
        );
    }

    return cellContent;
};

export default TruncatedCellContent;
